import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../../lib/api";
import { formatCLP, formatDateTime, apiError, formatThousands } from "../../lib/format";
import { simulate } from "../../lib/loanMath";
import { toast } from "sonner";
import { ArrowLeft, Plus, Minus, HandCoins, Calculator, Bank, FloppyDisk, LockKey, LockKeyOpen } from "@phosphor-icons/react";

export default function ClientDetail() {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [txs, setTxs] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [loans, setLoans] = useState([]);
  const [adjust, setAdjust] = useState({ amount: "", description: "" });
  const [offer, setOffer] = useState({ amount: "500000", cuotas: 12, rate: "1.2", comment: "" });
  const [bank, setBank] = useState({ enabled: false, nombre: "", rut: "", cuenta: "", tipo_cuenta: "Cuenta Corriente", valor: "", descripcion: "" });

  const load = () => {
    api.get(`/users/${id}`).then((r) => {
      setClient(r.data);
      const b = r.data.bank_data || {};
      setBank({
        enabled: !!b.enabled,
        nombre: b.nombre || "",
        rut: b.rut || "",
        cuenta: b.cuenta || "",
        tipo_cuenta: b.tipo_cuenta || "Cuenta Corriente",
        valor: formatThousands(b.valor || ""),
        descripcion: b.descripcion || "",
      });
    });
    api.get(`/users/${id}/transactions`).then((r) => setTxs(r.data));
    api.get(`/users/${id}/transfers`).then((r) => setTransfers(r.data));
    api.get(`/loans`, { params: { user_id: id } }).then((r) => setLoans(r.data));
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const offerSim = useMemo(
    () => simulate(offer.amount, offer.rate, offer.cuotas),
    [offer.amount, offer.rate, offer.cuotas]
  );

  const doAdjust = async (sign) => {
    const amt = Number(adjust.amount);
    if (!amt || amt <= 0) return toast.error("Monto requerido");
    if (!adjust.description) return toast.error("Describe el ajuste");
    try {
      await api.post(`/users/${id}/balance`, { amount: sign * amt, description: adjust.description });
      toast.success("Saldo actualizado");
      setAdjust({ amount: "", description: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doOffer = async (e) => {
    e.preventDefault();
    if (Number(offer.amount) < 500000) return toast.error("Monto mínimo $ 500.000");
    try {
      await api.post("/loans/offer", {
        client_id: id,
        amount: Number(offer.amount),
        cuotas: Number(offer.cuotas),
        rate: Number(offer.rate),
        comment: offer.comment,
      });
      toast.success("Crédito ofrecido");
      setOffer({ amount: "500000", cuotas: 12, rate: "1.2", comment: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const decide = async (loanId, action) => {
    try {
      await api.post(`/loans/${loanId}/${action}`, { comment: "" });
      toast.success("Hecho");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveBank = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/users/${id}/bank-data`, bank);
      toast.success(bank.enabled ? "Datos bancarios habilitados" : "Datos bancarios guardados");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleLoanAccess = async (enabled) => {
    try {
      await api.put(`/users/${id}/loan-access`, { enabled });
      toast.success(enabled ? "Créditos habilitados para el cliente" : "Créditos bloqueados");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!client) return <div className="text-inkMuted">Cargando…</div>;

  return (
    <div className="space-y-8">
      <Link to="/app/clientes" className="text-sm text-inkMuted hover:text-forest flex items-center gap-2"><ArrowLeft size={14} /> Volver</Link>

      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="label-mono mb-2">Cliente · {client.status}</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">{client.name}</h1>
          <div className="mt-2 font-mono text-sm text-inkMuted">{client.rut} · {client.email} · {client.phone}</div>
        </div>
        <div className="text-right">
          <div className="label-mono">Saldo actual</div>
          <div className="font-mono text-4xl font-medium" data-testid="client-balance">{formatCLP(client.balance)}</div>
        </div>
      </header>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Adjust balance */}
        <div className="bento p-6">
          <h2 className="font-display text-xl font-bold mb-4">Ajustar saldo</h2>
          <div className="space-y-3">
            <input data-testid="adjust-amount" type="number" value={adjust.amount} onChange={(e) => setAdjust({ ...adjust, amount: e.target.value })} placeholder="Monto" className="input font-mono" />
            <input data-testid="adjust-desc" value={adjust.description} onChange={(e) => setAdjust({ ...adjust, description: e.target.value })} placeholder="Descripción del movimiento" className="input" />
            <div className="flex gap-3">
              <button onClick={() => doAdjust(1)} className="flex-1 h-11 bg-success text-bg text-sm font-semibold flex items-center justify-center gap-2" data-testid="credit-btn"><Plus size={14} weight="bold" /> Abonar</button>
              <button onClick={() => doAdjust(-1)} className="flex-1 h-11 bg-burnt text-bg text-sm font-semibold flex items-center justify-center gap-2" data-testid="debit-btn"><Minus size={14} weight="bold" /> Cargar</button>
            </div>
          </div>
        </div>

        {/* Offer loan with live sim */}
        <form onSubmit={doOffer} className="bento p-6 space-y-3" data-testid="offer-form">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-display text-xl font-bold">Ofrecer crédito</h2>
            <Link
              to={`/app/simulador?client_id=${id}`}
              className="text-xs font-mono uppercase tracking-widest text-forest hover:text-burnt flex items-center gap-1"
              data-testid="go-full-simulator"
            >
              <Calculator size={12} weight="bold" /> Simulador completo →
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="label-mono">Monto · mín $500K</label><input data-testid="offer-amount" type="number" min="500000" step="50000" value={offer.amount} onChange={(e) => setOffer({ ...offer, amount: e.target.value })} className="input font-mono" /></div>
            <div><label className="label-mono">Cuotas · 3–220</label><input data-testid="offer-cuotas" type="number" min="3" max="220" value={offer.cuotas} onChange={(e) => setOffer({ ...offer, cuotas: e.target.value })} className="input font-mono" /></div>
            <div><label className="label-mono">Tasa % mes</label><input data-testid="offer-rate" type="number" step="0.05" value={offer.rate} onChange={(e) => setOffer({ ...offer, rate: e.target.value })} className="input font-mono" /></div>
          </div>

          {/* Live simulation preview */}
          <div className="grid grid-cols-3 border border-hairline bg-bg" data-testid="offer-sim-preview">
            <div className="p-3">
              <div className="label-mono">Cuota mensual</div>
              <div className="font-mono text-sm font-semibold mt-0.5">{formatCLP(Math.round(offerSim.monthly))}</div>
            </div>
            <div className="p-3 border-x border-hairline">
              <div className="label-mono">Total a pagar</div>
              <div className="font-mono text-sm mt-0.5">{formatCLP(Math.round(offerSim.total))}</div>
            </div>
            <div className="p-3">
              <div className="label-mono">Intereses</div>
              <div className="font-mono text-sm text-burnt mt-0.5">{formatCLP(Math.round(offerSim.interest))}</div>
            </div>
          </div>

          <input data-testid="offer-comment" value={offer.comment} onChange={(e) => setOffer({ ...offer, comment: e.target.value })} placeholder="Comentario" className="input" />
          <button className="h-11 px-4 bg-forest text-bg text-sm font-semibold flex items-center gap-2" data-testid="offer-submit"><HandCoins size={14} weight="bold" /> Enviar oferta</button>
        </form>
      </div>

      {/* Datos bancarios */}
      <form onSubmit={saveBank} className="bento p-6 space-y-4" data-testid="bank-data-form">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Bank size={20} weight="bold" className="text-forest" />
            <h2 className="font-display text-xl font-bold">Datos bancarios</h2>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              checked={bank.enabled}
              onChange={(e) => setBank({ ...bank, enabled: e.target.checked })}
              className="w-4 h-4 accent-forest"
              data-testid="bank-enabled"
            />
            <span className="label-mono">{bank.enabled ? "Habilitado" : "Deshabilitado"}</span>
          </label>
        </div>
        <p className="text-sm text-inkMuted">
          Al habilitar, el cliente verá estos datos de depósito en su dashboard. Mientras esté deshabilitado,
          verá el aviso {'"Datos bancarios pendientes de asignación"'}.
        </p>
        <div className="grid md:grid-cols-2 gap-3">
          <div><label className="label-mono">Nombre</label><input className="input" value={bank.nombre} onChange={(e) => setBank({ ...bank, nombre: e.target.value })} placeholder="Nombre del titular" data-testid="bank-nombre" /></div>
          <div><label className="label-mono">ID VIRTUAL</label><input className="input font-mono" value={bank.rut} onChange={(e) => setBank({ ...bank, rut: e.target.value })} placeholder="Ej: ID-0001234" data-testid="bank-rut" /></div>
          <div><label className="label-mono">N° de cuenta</label><input className="input font-mono" value={bank.cuenta} onChange={(e) => setBank({ ...bank, cuenta: e.target.value })} placeholder="00012345678" data-testid="bank-cuenta" /></div>
          <div>
            <label className="label-mono">Tipo de cuenta</label>
            <select className="input" value={bank.tipo_cuenta} onChange={(e) => setBank({ ...bank, tipo_cuenta: e.target.value })} data-testid="bank-tipo">
              <option>Cuenta Corriente</option>
              <option>Cuenta Vista</option>
              <option>Cuenta de Ahorro</option>
              <option>Chequera Electrónica</option>
            </select>
          </div>
          <div className="md:col-span-2"><label className="label-mono">Valor</label><input className="input font-mono" value={bank.valor} onChange={(e) => setBank({ ...bank, valor: formatThousands(e.target.value) })} inputMode="numeric" placeholder="Ej: 1.500.000" data-testid="bank-valor" /></div>
          <div className="md:col-span-2"><label className="label-mono">Descripción</label><textarea className="input" style={{ height: "auto", minHeight: "80px", padding: "10px 12px" }} rows={3} value={bank.descripcion} onChange={(e) => setBank({ ...bank, descripcion: e.target.value })} placeholder="Instrucciones o detalle del depósito (ej: Transferir a la cuenta indicada y enviar comprobante al chat)." data-testid="bank-descripcion" /></div>
        </div>
        <button className="h-11 px-4 bg-forest text-bg text-sm font-semibold flex items-center gap-2" data-testid="bank-save">
          <FloppyDisk size={14} weight="bold" /> Guardar datos bancarios
        </button>
      </form>

      {/* Acceso a créditos (activación cuenta vista) */}
      <div className="bento p-6" data-testid="loan-access-card">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 flex items-center justify-center bg-bg border border-hairline shrink-0">
              {client.loans_unlocked ? <LockKeyOpen size={22} weight="fill" className="text-success" /> : <LockKey size={22} weight="fill" className="text-warn" />}
            </div>
            <div>
              <h2 className="font-display text-xl font-bold">Solicitud de créditos</h2>
              <p className="text-sm text-inkMuted mt-1 max-w-xl">
                El cliente solo puede solicitar créditos cuando su cuenta vista está activada con un primer
                depósito electrónico. Habilita esta opción una vez confirmes el depósito.
              </p>
              <div className="mt-2">
                <span className={`label-mono ${client.loans_unlocked ? "text-success" : "text-warn"}`} data-testid="loan-access-status">
                  {client.loans_unlocked ? "Habilitado" : "Bloqueado · pendiente de activación"}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={() => toggleLoanAccess(!client.loans_unlocked)}
            className={`h-11 px-4 text-sm font-semibold text-bg shrink-0 flex items-center gap-2 ${client.loans_unlocked ? "bg-burnt hover:bg-burnt-hover" : "bg-forest hover:bg-forest-hover"}`}
            data-testid="loan-access-toggle"
          >
            {client.loans_unlocked ? (<><LockKey size={14} weight="bold" /> Bloquear créditos</>) : (<><LockKeyOpen size={14} weight="bold" /> Habilitar créditos</>)}
          </button>
        </div>
      </div>

      {/* Loans */}
      <div className="bento">
        <div className="px-6 py-4 border-b border-hairline"><h2 className="font-display text-xl font-bold">Créditos</h2></div>
        <table className="w-full text-sm">
          <thead><tr className="label-mono border-b border-hairline">
            <th className="px-6 py-3 text-left">Fecha</th><th className="px-6 py-3 text-left">Origen</th><th className="px-6 py-3 text-left">Monto</th><th className="px-6 py-3 text-left">Cuotas</th><th className="px-6 py-3 text-left">Estado</th><th className="px-6 py-3 text-right">Acciones</th>
          </tr></thead>
          <tbody>
            {loans.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-inkMuted text-sm">Sin créditos.</td></tr>}
            {loans.map((l) => (
              <tr key={l.id} className="border-b border-hairline">
                <td className="px-6 py-3 font-mono text-xs">{formatDateTime(l.created_at)}</td>
                <td className="px-6 py-3">{l.origin}</td>
                <td className="px-6 py-3 font-mono">{formatCLP(l.amount)}</td>
                <td className="px-6 py-3">{l.cuotas}</td>
                <td className="px-6 py-3"><span className="label-mono">{l.status}</span></td>
                <td className="px-6 py-3 text-right space-x-2">
                  {(l.status === "pendiente" || l.status === "ofrecido") && (
                    <>
                      <button onClick={() => decide(l.id, "approve")} className="h-9 px-3 bg-success text-bg text-xs" data-testid={`approve-${l.id}`}>Aprobar</button>
                      <button onClick={() => decide(l.id, "reject")} className="h-9 px-3 border border-danger text-danger text-xs" data-testid={`reject-${l.id}`}>Rechazar</button>
                    </>
                  )}
                  {l.status === "aprobado" && (
                    <button onClick={() => decide(l.id, "disburse")} className="h-9 px-3 bg-burnt text-bg text-xs" data-testid={`disburse-${l.id}`}>Desembolsar</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Transactions */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bento">
          <div className="px-6 py-4 border-b border-hairline"><h3 className="font-display text-lg font-bold">Movimientos</h3></div>
          <div className="max-h-[400px] overflow-auto">
            {txs.map((t) => (
              <div key={t.id} className="px-6 py-3 border-b border-hairline flex items-center justify-between text-sm">
                <div><div>{t.description}</div><div className="label-mono">{formatDateTime(t.created_at)}</div></div>
                <div className={`font-mono ${t.type === "credit" ? "text-success" : "text-burnt"}`}>{t.type === "credit" ? "+" : "−"} {formatCLP(t.amount)}</div>
              </div>
            ))}
            {txs.length === 0 && <div className="p-6 text-sm text-inkMuted">Sin movimientos.</div>}
          </div>
        </div>
        <div className="bento">
          <div className="px-6 py-4 border-b border-hairline"><h3 className="font-display text-lg font-bold">Transferencias enviadas</h3></div>
          <div className="max-h-[480px] overflow-auto">
            {transfers.map((t) => (
              <details key={t.id} className="border-b border-hairline group" data-testid={`transfer-${t.id}`}>
                <summary className="px-6 py-3 text-sm cursor-pointer hover:bg-bg list-none">
                  <div className="flex justify-between items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{t.recipient_name}</div>
                      <div className="label-mono mt-0.5">{t.bank} · {formatDateTime(t.created_at)}</div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="font-mono text-burnt">− {formatCLP(t.amount)}</span>
                      <span className="text-inkMuted group-open:rotate-90 transition-transform text-xs">›</span>
                    </div>
                  </div>
                </summary>
                <div className="px-6 pb-4 pt-1 bg-bg/50 grid grid-cols-2 gap-x-4 gap-y-2 text-xs font-mono">
                  <Row label="Origen" value={`${client.name} · ${client.rut}`} />
                  <Row label="Destinatario" value={t.recipient_name} />
                  <Row label="RUT destino" value={t.recipient_rut} />
                  <Row label="Email destino" value={t.recipient_email} />
                  <Row label="Banco" value={t.bank} />
                  <Row label="Tipo cuenta" value={t.account_type} />
                  <Row label="N° cuenta" value={t.account_number} />
                  <Row label="Monto" value={formatCLP(t.amount)} />
                  {t.comment && <Row label="Comentario" value={t.comment} full />}
                  <Row label="ID transferencia" value={t.id} mono full />
                  <Row label="Fecha" value={formatDateTime(t.created_at)} full />
                </div>
              </details>
            ))}
            {transfers.length === 0 && <div className="p-6 text-sm text-inkMuted">Sin transferencias.</div>}
          </div>
        </div>
      </div>

      <style>{`.input { width: 100%; height: 46px; padding: 0 12px; border: 1px solid rgba(10,37,64,0.2); background: #fff; font-size: 14px; } .input:focus{outline:none;border-color:#0A2540;}`}</style>
    </div>
  );
}

function Row({ label, value, full = false, mono = false }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-widest text-inkMuted">{label}</div>
      <div className={`mt-0.5 break-words ${mono ? "text-[10px]" : "text-xs"}`}>{value || "—"}</div>
    </div>
  );
}
